import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  Badge,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  Tooltip,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, CopyIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useApi } from '@/contexts/ApiContext';
import { CordeCommonModelsDtoNotificationsNotificationTemplate } from '@/api/generated/v1/api';
import { CreateTemplateModal } from '@/components/notifications/CreateTemplateModal';

export const NotificationTemplatesTab: React.FC = () => {
  const { t } = useTranslation();
  const api = useApi();
  const toast = useToast();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  // State
  const [templates, setTemplates] = useState<CordeCommonModelsDtoNotificationsNotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<CordeCommonModelsDtoNotificationsNotificationTemplate | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Theme colors
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Load templates
  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.NotificationsApi.listTemplates();
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.loadTemplatesError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // Delete template
  const handleDeleteTemplate = async () => {
    if (!selectedTemplate?.id) return;

    try {
      setDeleteLoading(true);
      await api.NotificationsApi.deleteTemplate(selectedTemplate.id);
      
      setTemplates(prev => prev.filter(template => template.id !== selectedTemplate.id));
      
      toast({
        title: t('common.success'),
        description: t('notifications.templateDeleted'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onDeleteClose();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.deleteTemplateError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Duplicate template
  const handleDuplicateTemplate = async (template: CordeCommonModelsDtoNotificationsNotificationTemplate) => {
    try {
      await api.NotificationsApi.createTemplate({
        template: {
          name: `${template.name} (Copy)`,
          description: template.description,
          channel_type: template.channel_type,
          body_template: template.body_template,
          subject_template: template.subject_template,
          language: template.language,
          required_variables: template.required_variables,
        }
      });
      
      toast({
        title: t('common.success'),
        description: t('notifications.templateDuplicated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      loadTemplates();
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.duplicateTemplateError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box>
        <Flex justify="center" align="center" h="200px">
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="lg" fontWeight="semibold">
          {t('notifications.templatesManagement')}
        </Text>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="blue"
          onClick={onCreateOpen}
        >
          {t('notifications.createTemplate')}
        </Button>
      </Flex>

      {templates.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          {t('notifications.noTemplates')}
        </Alert>
      ) : (
        <Box border="1px" borderColor={borderColor} borderRadius="md">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>{t('notifications.name')}</Th>
                <Th>{t('notifications.channelType')}</Th>
                <Th>{t('notifications.language')}</Th>
                <Th>{t('notifications.version')}</Th>
                <Th>{t('notifications.created')}</Th>
                <Th>{t('common.actions')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {templates.map((template) => (
                <Tr key={template.id}>
                  <Td>
                    <Box>
                      <Text fontWeight="semibold">{template.name || t('notifications.unnamedTemplate')}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {template.description || t('notifications.noDescription')}
                      </Text>
                    </Box>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue">{template.channel_type || 'unknown'}</Badge>
                  </Td>
                  <Td>
                    <Badge variant="outline">{template.language || 'en'}</Badge>
                  </Td>
                  <Td>
                    <Text>{template.version || 1}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {template.created_at ? new Date(template.created_at).toLocaleDateString() : 'N/A'}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label={t('notifications.duplicate')}>
                        <IconButton
                          aria-label={t('notifications.duplicate')}
                          icon={<CopyIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDuplicateTemplate(template)}
                        />
                      </Tooltip>
                      <Tooltip label={t('common.delete')}>
                        <IconButton
                          aria-label={t('common.delete')}
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => {
                            setSelectedTemplate(template);
                            onDeleteOpen();
                          }}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Create Template Modal */}
      <CreateTemplateModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onSuccess={loadTemplates}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('notifications.deleteTemplate')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('notifications.deleteTemplateConfirm', { name: selectedTemplate?.name })}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteTemplate}
                ml={3}
                isLoading={deleteLoading}
              >
                {t('common.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}; 